// import AdapterDateFns from '@mui/lab/AdapterDateFns'
import { CssBaseline } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import assign from 'lodash/assign'
import { SnackbarProvider } from 'notistack'
import React, { Suspense, lazy } from 'react'
import { createRoot } from 'react-dom/client'
import { Provider as ReduxProvider } from 'react-redux'
import { SWRConfig, SWRConfiguration } from 'swr'

import { AppContextProps, AppContextProvider } from './AppContext'
import ErrorTracing from './ErrorTracing'
import PublicGlobals from './PublicGlobals'
import ThemeProvider from './ThemeProvider'
import AuthGate from './auth/AuthGate'
import AppWrapper from './components/common/AppWrapper'
import LoadingIndicator from './components/common/LoadingIndicator'
import Toast from './components/common/Toast'
import registerServiceWorker from './registerServiceWorker'
import store from './store'
import TenantProvider from './tenant/TenantProvider'
import fetcher from './swr/fetchers/fetcher'
import addsLoading from './swr/middleware/addsLoading'

const ErrorPage = lazy(() => import('./pages/ErrorPage'))
const LoggedInUI = lazy(() => import('./pages/LoggedInUI'))
const LoginPage = lazy(() => import('./pages/LoginPage'))

const pages = {
  ErrorPage,
  LoggedInUI,
  LoginPage,
}

const container = document.getElementById('root')

const swrConfig: SWRConfiguration = {
  dedupingInterval: 5000,
  revalidateOnFocus: false,
  use: [addsLoading],
  fetcher,
}

export class ClientAppRenderer {
  static async renderApp(
    pageName: string,
    pageProps: any,
    publicGlobals?: any,
    appContext?: AppContextProps
  ) {
    let localContext = { ...appContext } as any
    localContext.setAppContext = (val) => (localContext = val)

    if (publicGlobals) {
      assign(PublicGlobals, publicGlobals)
    }

    const reactPageType = pages[pageName]
    const mainElement = React.createElement(reactPageType, pageProps)
    return (
      <SWRConfig value={swrConfig}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>{mainElement}</LocalizationProvider>
      </SWRConfig>
    )
  }

  static async renderClientApp(
    pageName: string,
    pageProps: any,
    publicGlobals?: any,
    appContext?: AppContextProps
  ) {
    let localContext = { ...appContext } as any
    localContext.setAppContext = (val) => (localContext = val)
    const reactPage = await ClientAppRenderer.renderApp(
      pageName,
      pageProps,
      publicGlobals,
      localContext
    )

    createRoot(container).render(
      <ErrorTracing>
        <CssBaseline>
          <Suspense fallback={<LoadingIndicator size="large" />}>
            <ThemeProvider>
              <TenantProvider>
                <SnackbarProvider
                  Components={{
                    default: Toast,
                    error: Toast,
                    info: Toast,
                    success: Toast,
                    warning: Toast,
                  }}
                  maxSnack={3}
                >
                  <ReduxProvider store={store}>
                    <AppContextProvider {...localContext}>
                      <AuthGate>
                        <AppWrapper authenticated>{reactPage}</AppWrapper>
                      </AuthGate>
                    </AppContextProvider>
                  </ReduxProvider>
                </SnackbarProvider>
              </TenantProvider>
            </ThemeProvider>
          </Suspense>
        </CssBaseline>
      </ErrorTracing>
    )
  }
}

if (typeof window !== 'undefined') {
  ;(window as any).renderClientApp = ClientAppRenderer.renderClientApp
}

/**
 * Register service worker
 * This worker is responsible for caching webviewer files
 */
registerServiceWorker()
